import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { Accordion } from '@zillow/constellation-10';

import {
  emitTenantAccordionItemExpandClickEvent,
  TENANT_ACCORDION_ITEM_EXPAND_CLICK_EVENT_ID,
} from '../../../../analytics/events/tenantDirectory';
import { useAuth } from '../../../AuthProvider';
import TenantAccordionItem from './TenantAccordionItem';

import type {
  RentalManagerRenterContactTenantWithTenanciesDto,
  RentalManagerRenterUpdateRenterResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { DatarouterError } from '@zg-rentals/web-base';

type Props = {
  listingAlias: string;
  tenants: Array<RentalManagerRenterContactTenantWithTenanciesDto>;
  onDeleteTenancyPeriodSuccess?: () => void | null;
  onDeleteTenancyPeriodError?: (error: DatarouterError) => void | null;
  onDeleteTenantSuccess?: () => void | null;
  onDeleteTenantError?: (error: DatarouterError) => void | null;
  onEditTenantSuccess?: (updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => void | null;
  onLeaseTermActionSuccess?: ({
    addedRenters,
    isEditMode,
  }: {
    addedRenters?: Array<RenterInfoRenterAddedDto>;
    isEditMode: boolean;
  }) => void | null;
  onLeaseTermActionError?: (error: DatarouterError) => void | null;
  onEditTenantError?: (error: DatarouterError) => void | null;
  onRequestProofSuccess?: (message: string) => void | null;
  onRequestProofError?: (message: string) => void | null;
};

const TenantAccordionList = ({
  listingAlias,
  tenants,
  onDeleteTenancyPeriodSuccess,
  onDeleteTenancyPeriodError,
  onDeleteTenantSuccess,
  onDeleteTenantError,
  onEditTenantSuccess,
  onLeaseTermActionSuccess,
  onEditTenantError,
  onLeaseTermActionError,
  onRequestProofSuccess,
  onRequestProofError,
}: Props) => {
  const { user } = useAuth();
  const router = useRouter();

  const renterId = router.query.renterId as string | undefined;
  const targetTenantIndex = renterId ? tenants.findIndex((tenant) => tenant.renterId === renterId) : 0;
  const defaultExpandedValue = [`tenant-${targetTenantIndex}`];

  const previousExpandedRef = useRef<Array<string>>(defaultExpandedValue);

  useEffect(() => {
    if (renterId) {
      const rafId = requestAnimationFrame(() => {
        const accordionHeader = document.getElementById(`tenant-${targetTenantIndex}-header`);
        if (accordionHeader) {
          accordionHeader.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });

      return () => cancelAnimationFrame(rafId);
    }
  }, [renterId, targetTenantIndex]);

  const handleExpandedChange = (value: string | Array<string>) => {
    const currentExpanded = Array.isArray(value) ? value : [value];
    const previousExpanded = previousExpandedRef.current;

    const newlyExpanded = currentExpanded.filter((item) => !previousExpanded.includes(item));

    if (newlyExpanded.length > 0 && user) {
      newlyExpanded.forEach(() => {
        emitTenantAccordionItemExpandClickEvent({
          user,
          currentProperty: { listingAlias },
        });
      });
    }

    previousExpandedRef.current = currentExpanded;
  };

  return (
    <Accordion.Root
      defaultExpanded={defaultExpandedValue}
      data-testid="TenantListAccordion"
      data-eventid={TENANT_ACCORDION_ITEM_EXPAND_CLICK_EVENT_ID}
      onExpandedChange={handleExpandedChange}
    >
      {tenants.map((tenant, index) => (
        <TenantAccordionItem
          key={index}
          listingAlias={listingAlias}
          tenant={tenant}
          index={index}
          onDeleteTenancyPeriodSuccess={onDeleteTenancyPeriodSuccess}
          onDeleteTenancyPeriodError={onDeleteTenancyPeriodError}
          onDeleteTenantSuccess={onDeleteTenantSuccess}
          onDeleteTenantError={onDeleteTenantError}
          onEditTenantSuccess={onEditTenantSuccess}
          onEditTenantError={onEditTenantError}
          onLeaseTermActionSuccess={onLeaseTermActionSuccess}
          onLeaseTermActionError={onLeaseTermActionError}
          onRequestProofSuccess={onRequestProofSuccess}
          onRequestProofError={onRequestProofError}
        />
      ))}
    </Accordion.Root>
  );
};

export default TenantAccordionList;
